import React from 'react';
import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
export interface Testimonial {
  initials: string;
  name: string;
  location: string;
  text: string;
  color: string;
  pack?: string;
}
interface TestimonialsProps {
  testimonials: Testimonial[];
  title?: string;
  className?: string;
}
export function Testimonials({
  testimonials,
  title,
  className = ''
}: TestimonialsProps) {
  return <section className={`py-16 bg-[#ece2cd] ${className}`} data-id="element-309">
      <div className="container mx-auto px-4" data-id="element-310">
        {title && <h2 className="font-black text-center mb-12 text-[#1e4230] text-[18px] md:text-2xl font-[Merriweather,_serif]" data-id="element-311">
            {title}
          </h2>}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto" data-id="element-312">
          {testimonials.map((t, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: i * 0.1
        }} className="bg-white p-6 rounded-2xl shadow-[0_8px_24px_-12px_rgba(31,50,39,0.25)] border border-[#e0d6bf] flex flex-col h-full" data-id="element-313">
              <div className="flex items-center gap-1 mb-4" data-id="element-314">
                {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 text-[#c8971b] fill-[#c8971b]" data-id="element-315" />)}
              </div>

              <p className="text-[#4a4133] italic mb-6 flex-grow leading-relaxed" data-id="element-316">
                "{t.text}"
              </p>

              <div className="flex items-center gap-3 mt-auto pt-4 border-t border-[#e0d6bf]" data-id="element-317">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${t.color}`} data-id="element-318">
                  {t.initials}
                </div>
                <div data-id="element-319">
                  <p className="font-bold text-[#1e4230] text-sm" data-id="element-320">{t.name}</p>
                  <p className="text-xs text-[#6f4e2e]" data-id="element-321">{t.location}</p>
                  {t.pack && <p className="text-xs font-semibold text-[#a97e12] mt-0.5" data-id="element-322">
                      {t.pack}
                    </p>}
                </div>
              </div>
            </motion.div>)}
        </div>
      </div>
    </section>;
}
export const initialTestimonials: Testimonial[] = [{
  initials: 'CR',
  name: 'Carolina R.',
  location: 'Profesora de Ciencias Naturales · Escuela secundaria, Córdoba',
  text: 'Llevaba años enseñando Gestión Ambiental con artículos sueltos de internet que yo misma buscaba y armaba. Abrí el Tomo 3 de Gestión de Residuos y en una tarde tenía más material contextualizado en Argentina que en toda mi carrera docente. Ahora mis alumnos me preguntan sobre el Riachuelo, el litio de la Puna y la deforestación del Chaco, y tengo respuesta fundamentada para todo.',
  color: 'bg-[#2f6b47]'
}, {
  initials: 'MT',
  name: 'Marcos T.',
  location: 'Docente de Geografía · Terciario, Neuquén',
  text: 'El Tomo 5 de Cambio Climático y Sostenibilidad me dio el sistema que necesitaba para conectar la materia con la realidad del país. Mis alumnos por primera vez entendieron por qué Argentina tiene que tomar decisiones urgentes sobre Vaca Muerta, los glaciares y las energías renovables. Lo implementé en dos semanas y los cambios en el aula fueron inmediatos.',
  color: 'bg-[#6f4e2e]'
}, {
  initials: 'LP',
  name: 'Laura P.',
  location: 'Profesora de Gestión Ambiental · Instituto de Formación Docente, Jujuy',
  text: 'Las actividades del Bonus 1 las uso en cada clase práctica. Mis alumnos trabajan con material profesional y contextualizado, y eso genera un nivel de compromiso que nunca había visto. Nunca pensé que algo tan concreto iba a cambiar tanto mis clases.',
  color: 'bg-[#1e4230]'
}];
export const moreTestimonials: Testimonial[] = [{
  initials: 'FM',
  name: 'Federico M.',
  location: '🏙️ Buenos Aires',
  text: 'Dos años enseñando Gestión Ambiental con los mismos ejemplos genéricos de los libros. Con el Megapack en una tarde tenía el argumento pedagógico para cada problemática con un ejemplo argentino concreto. Mis alumnos empezaron a preguntar más que nunca.',
  color: 'bg-[#2f6b47]',
  pack: 'Megapack Gestión Ambiental'
}, {
  initials: 'RV',
  name: 'Rosa V.',
  location: '🏙️ Santa Fe',
  text: 'El Tomo 5 de Cambio Climático me dio el enfoque perfecto para conectar la materia con la realidad del país. Cuando explicás los glaciares que se derriten, el litio de la Puna y Vaca Muerta en la misma clase, los alumnos se enganchan solos. Mis clases cambiaron completamente.',
  color: 'bg-[#6f4e2e]',
  pack: 'Megapack Gestión Ambiental'
}, {
  initials: 'GN',
  name: 'Gabriela N.',
  location: '🏙️ Tucumán',
  text: 'Con el Bonus de Efemérides empecé a tener actos ambientales completos sin prepararlos de cero. El Día del Agua, el Día de la Tierra, el Día del Árbol — todo tiene guión, actividades y campaña de redes lista. En un mes mis alumnos participaban más y los resultados mejoraron visiblemente.',
  color: 'bg-[#a97e12]',
  pack: 'Megapack + Nivel Experto'
}, {
  initials: 'AS',
  name: 'Alejandro S.',
  location: '🏙️ Rosario',
  text: 'Cerré la planificación anual de Educación Ambiental en dos horas usando los 6 Tomos como guía. Antes me llevaba semanas y siempre quedaba algo sin resolver. Ahora tengo todo el año organizado, con legislación actualizada y contextualizado en Argentina.',
  color: 'bg-[#2f6b47]',
  pack: 'Megapack + Nivel Experto'
}, {
  initials: 'SM',
  name: 'Sofía M.',
  location: '🏙️ Córdoba',
  text: 'El Bonus de Proyectos lo uso desde el primer trimestre. Los 6 proyectos listos — huerta, compostaje, reciclaje, agua, energía y forestación — me dieron la estructura que necesitaba para que mis alumnos hagan algo real. Mi escuela hoy tiene una compostera funcionando y un bosque nativo con 30 árboles. Eso no tiene precio.',
  color: 'bg-[#1e4230]',
  pack: 'Megapack Gestión Ambiental'
}];